import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    MenuItem,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    TablePagination,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { API_URL } from '../../api';
import { shortYear, YEAR_GROUP_OPTIONS } from '../../constants/yearGroups';
import HowItWorksCard from '../../components/HowItWorksCard';
import { useConfirm } from '../../components/ConfirmDialog';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const STATUS_OPTIONS = [
    { value: '', label: 'Any status' },
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'converted', label: 'Account created' },
    { value: 'enrolled', label: 'Enrolled in a class' },
    { value: 'declined', label: 'Declined' },
    { value: 'archived', label: 'Archived' },
];
const STATUS_COLOR = {
    new: 'primary',
    contacted: 'info',
    converted: 'warning',
    enrolled: 'success',
    declined: 'default',
    archived: 'default',
};

// Position each status on the 0..3 progress axis so the dialog can show
// a stepper (Received → Contacted → Account created → Enrolled).
const STATUS_STEP = { new: 0, contacted: 1, converted: 2, enrolled: 3, declined: -1, archived: -1 };

const statusLabel = (v) => STATUS_OPTIONS.find((o) => o.value === v)?.label || v;

const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');

export default function WaitingListPage() {
    const confirm = useConfirm();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [unread, setUnread] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const [q, setQ] = useState('');
    const [status, setStatus] = useState('');
    const [unreadOnly, setUnreadOnly] = useState(false);

    const [detail, setDetail] = useState(null);
    const [classes, setClasses] = useState([]);
    const [enrolClassId, setEnrolClassId] = useState('');
    const [enrolBusy, setEnrolBusy] = useState(false);

    const [newOpen, setNewOpen] = useState(false);
    const [newForm, setNewForm] = useState(emptyNewForm());
    const [newBusy, setNewBusy] = useState(false);
    const [newError, setNewError] = useState('');

    useEffect(() => {
        // Pre-load published classes once so the dialog dropdown is instant.
        axios
            .get(`${API_URL}/api/admin/tutoring-classes?status=published`, authHeaders())
            .then((r) => setClasses(Array.isArray(r.data) ? r.data : []))
            .catch(() => {});
    }, []);

    const buildQuery = () => {
        const params = new URLSearchParams();
        params.set('page', page + 1);
        params.set('limit', rowsPerPage);
        if (q.trim()) params.set('q', q.trim());
        if (status) params.set('status', status);
        if (unreadOnly) params.set('read', 'false');
        return params;
    };

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${API_URL}/api/admin/waiting-list?${buildQuery().toString()}`,
                authHeaders()
            );
            setItems(data.items || []);
            setTotal(data.total || 0);
            setUnread(data.unreadCount || 0);
        } catch {
            setError('Failed to load.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, rowsPerPage]);

    const submit = (e) => {
        e.preventDefault();
        if (page !== 0) setPage(0);
        else load();
    };

    const openDetail = async (id) => {
        try {
            const { data } = await axios.get(`${API_URL}/api/admin/waiting-list/${id}`, authHeaders());
            setDetail(data);
            setEnrolClassId('');
            if (!data.read) {
                await axios.patch(`${API_URL}/api/admin/waiting-list/${id}`, { read: true }, authHeaders());
                load();
            }
        } catch {
            setError('Failed to load entry.');
        }
    };

    const enrol = async () => {
        if (!detail?._id || !enrolClassId) return;
        setEnrolBusy(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/admin/waiting-list/${detail._id}/enroll`,
                { tutoringClassId: enrolClassId },
                authHeaders()
            );
            const klassName = data.class?.name || 'class';
            const lessonNote = data.lessons?.upserted
                ? ` ${data.lessons.upserted} lesson row${data.lessons.upserted === 1 ? '' : 's'} ready in the register.`
                : '';
            const accountNote = data.reusedAccount ? '' : ' New parent account created.';
            const alreadyNote = data.alreadyEnrolled ? ' (Already on the roster — nothing to add.)' : '';
            setMessage(`Enrolled in ${klassName}.${accountNote}${lessonNote}${alreadyNote}`);
            // Re-fetch so `convertedToAccountId` comes back populated with the parent's email.
            const { data: fresh } = await axios.get(`${API_URL}/api/admin/waiting-list/${detail._id}`, authHeaders());
            setDetail(fresh);
            setEnrolClassId('');
            load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to enrol.');
        } finally {
            setEnrolBusy(false);
        }
    };

    const updateStatus = async (id, newStatus) => {
        try {
            const { data } = await axios.patch(
                `${API_URL}/api/admin/waiting-list/${id}`,
                { status: newStatus },
                authHeaders()
            );
            setDetail(data);
            load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        }
    };

    const convert = async (id) => {
        if (!(await confirm({
            title: 'Create parent account',
            message:
                'The parent will be emailed a welcome link to set their own password. ' +
                'Their account is created as Approved (no separate approval step) ' +
                'and this waiting-list entry will archive itself the first time they log in.',
            confirmLabel: 'Create account',
        }))) return;
        try {
            const { data } = await axios.post(
                `${API_URL}/api/admin/waiting-list/${id}/convert`,
                {},
                authHeaders()
            );
            setMessage(data.reused
                ? `Linked to existing parent account (${data.account?.email}). No welcome email sent — that account already existed.`
                : `Created parent account for ${data.account?.email}. Welcome email sent — they'll set their password from the link.`);
            // Re-fetch so `convertedToAccountId` comes back populated with the parent's email.
            const { data: fresh } = await axios.get(`${API_URL}/api/admin/waiting-list/${id}`, authHeaders());
            setDetail(fresh);
            load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to convert.');
        }
    };

    const openNew = () => {
        setNewForm(emptyNewForm());
        setNewError('');
        setNewOpen(true);
    };

    const submitNew = async () => {
        if (!newForm.firstName.trim() || !newForm.surname.trim() || !newForm.email.trim()) {
            setNewError('First name, surname and email are required.');
            return;
        }
        setNewBusy(true);
        setNewError('');
        try {
            await axios.post(`${API_URL}/api/admin/waiting-list`, newForm, authHeaders());
            setNewOpen(false);
            setMessage(
                newForm.sendThankYou
                    ? `Added ${newForm.firstName} to the waiting list and sent thank-you email.`
                    : `Added ${newForm.firstName} to the waiting list.`
            );
            load();
        } catch (err) {
            setNewError(err?.response?.data?.message || 'Failed to add.');
        } finally {
            setNewBusy(false);
        }
    };

    const remove = async (id) => {
        if (!(await confirm({ title: 'Delete entry', message: 'Delete this waiting-list entry?', confirmLabel: 'Delete', danger: true }))) return;
        try {
            await axios.delete(`${API_URL}/api/admin/waiting-list/${id}`, authHeaders());
            setDetail(null);
            load();
        } catch {
            setError('Failed to delete.');
        }
    };

    return (
        <Container>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                sx={{ mb: 3, gap: 2, flexWrap: 'wrap', rowGap: 2 }}
            >
                <Box sx={{ maxWidth: 760 }}>
                    <Typography variant="h4">
                        Waiting list
                        {unread > 0 && <Chip label={`${unread} unread`} color="error" size="small" sx={{ ml: 2 }} />}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 760 }}>
                        Parents who ticked "Also add me to the waiting list" on the Contact form (or
                        an enquiry that came in directly).
                    </Typography>
                    
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openNew}
                    sx={{
                        padding: '8px 22px',
                        fontFamily: '"Inter"',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        color: '#fff !important',
                    }}
                >
                    New waiting-list entry
                </Button>
            </Stack>
            <Box sx={{ mb: 3 }}>
                <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>1.</strong> Review the enquiry, call the parent, then update the status
                            to <em>Contacted</em>.<br />
                            <strong>2.</strong> When a space becomes available, open the waiting-list entry
                            and choose the class from the dropdown, then click{' '}
                            <strong>Create account &amp; enrol</strong>. This creates the parent account
                            (if it doesn't already exist), adds the child to that class's register, and
                            generates the lesson rows for the term — the whole process happens in one go.<br />
                            <strong>3.</strong> <em>Create parent account</em> creates the parent
                            account now so you can enrol the student into a Tutoring class later.<br />
                            <strong>What the parent sees:</strong> the new account is created as{' '}
                            <em>Approved</em> (no separate approval step — you've already vetted them
                            here) and the parent is emailed a "set your password" link. This waiting-list
                            entry archives itself the first time they log in, so the active view only
                            shows work-in-progress.<br />
                            <strong>Students:</strong> only the parent account is created here. The parent
                            (or admin, from the parent's detail page) then invites the student to sign up
                            as a linked account from the <em>Linked accounts</em> card. Students never come
                            in through the waiting list on their own.
                        </Typography>
                    </HowItWorksCard>
            </Box>


            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

            <Card sx={{ p: 2, mb: 3 }}>
                <Box component="form" onSubmit={submit} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        label="Search name / email / child / school"
                        size="small"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        sx={{ minWidth: 280 }}
                    />
                    <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
                        {STATUS_OPTIONS.map((o) => (
                            <MenuItem key={o.value || 'any'} value={o.value}>{o.label}</MenuItem>
                        ))}
                    </TextField>
                    <Button type="submit" variant="contained">Filter</Button>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />
                        <Typography variant="body2">Unread only</Typography>
                    </Box>
                </Box>
            </Card>

            <Card>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                ) : (
                    <>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Received</TableCell>
                                    <TableCell>Parent</TableCell>
                                    <TableCell>Child</TableCell>
                                    <TableCell>Year</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((it) => (
                                    <TableRow key={it._id} sx={{ background: it.read ? 'inherit' : 'rgba(0,153,255,0.05)' }}>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography variant="body2">{fmt(it.createdAt)}</Typography>
                                                {!it.read && <Chip size="small" label="New" color="error" />}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{it.firstName} {it.surname}</Typography>
                                            <Typography variant="caption" color="text.secondary">{it.email}</Typography>
                                        </TableCell>
                                        <TableCell>{it.childName || '—'}</TableCell>
                                        <TableCell>{shortYear(it.yearGroup) || '—'}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={statusLabel(it.status)} color={STATUS_COLOR[it.status] || 'default'} />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button size="small" onClick={() => openDetail(it._id)}>Open</Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {items.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                                No waiting list entries.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        />
                    </>
                )}
            </Card>

            <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {detail ? (
                        <>
                            {detail.firstName} {detail.surname}
                            <Chip size="small" label={statusLabel(detail.status)} color={STATUS_COLOR[detail.status] || 'default'} sx={{ ml: 2 }} />
                        </>
                    ) : ''}
                </DialogTitle>
                <DialogContent dividers>
                    {detail && (
                        <Stack spacing={2}>
                            <FlowStepper status={detail.status} />

                            <Stack spacing={1.5}>
                                <Row label="Email" value={detail.email} />
                                <Row label="Phone" value={detail.phone} />
                                <Divider />
                                <Row label="Child's name" value={detail.childName} />
                                <Row label="Year group" value={detail.yearGroup} />
                                <Row label="Subject" value={detail.subject} />
                                <Row label="School" value={detail.school} />
                                <Row label="How did they hear?" value={detail.howHeard} />
                                <Divider />
                                <Row label="Comments" value={detail.comments} multi />
                            </Stack>

                            {detail.convertedToAccountId && (
                                detail.status === 'converted' || detail.status === 'enrolled' ? (
                                    <Alert severity="info">
                                        Parent account:{' '}
                                        <strong>{detail.convertedToAccountId.email || detail.email}</strong>
                                        {' '}— account is <em>Approved</em>; a welcome email with a
                                        set-password link was sent to the parent. This entry will
                                        archive automatically the first time they log in.
                                    </Alert>
                                ) : (
                                    <Alert severity="info">
                                        A parent account with this email already exists on the
                                        platform:{' '}
                                        <strong>{detail.convertedToAccountId.email || detail.email}</strong>.
                                        {' '}No welcome email is sent for existing accounts — if the
                                        parent needs sign-in help, they can use <em>Forgot password</em>{' '}
                                        from the login page, or you can send them a reset link from
                                        their account page.
                                    </Alert>
                                )
                            )}

                            {detail.status !== 'enrolled' && detail.status !== 'declined' && detail.status !== 'archived' && (
                                <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                        Create account &amp; enrol in one step
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                        Picks a published class, creates the <strong>parent account</strong>{' '}
                                        (if needed), adds the child to that class's roster, and generates lesson
                                        rows for every date in the class's term(s).
                                    </Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
                                        <TextField
                                            select size="small" label="Class"
                                            value={enrolClassId}
                                            onChange={(e) => setEnrolClassId(e.target.value)}
                                            sx={{ flex: 1, minWidth: 240 }}
                                            disabled={!classes.length}
                                        >
                                            {classes.length === 0 && (
                                                <MenuItem value="" disabled>
                                                    No published classes yet — publish one in Tutoring classes first.
                                                </MenuItem>
                                            )}
                                            {classes.map((c) => (
                                                <MenuItem key={c._id} value={c._id}>
                                                    {c.name} ({c.yearGroup || '—'} · {c.subject || '—'})
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <Button
                                            variant="contained"
                                            onClick={enrol}
                                            disabled={!enrolClassId || enrolBusy}
                                        >
                                            {enrolBusy ? 'Enrolling…' : 'Create account & enrol'}
                                        </Button>
                                    </Stack>
                                </Box>
                            )}

                            {detail.status === 'enrolled' && (
                                <Alert severity="success">
                                    This entry is fully actioned — the child is on a class roster. You
                                    can manage their enrolment from <em>Tutoring classes</em> if anything
                                    needs to change.
                                </Alert>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    {detail && (
                        <Stack direction="row" spacing={1} sx={{ width: '100%', justifyContent: 'space-between' }}>
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    select size="small" label="Set status"
                                    value={detail.status}
                                    onChange={(e) => updateStatus(detail._id, e.target.value)}
                                    sx={{ minWidth: 200 }}
                                >
                                    {STATUS_OPTIONS.filter((o) => o.value).map((o) => (
                                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                    ))}
                                </TextField>
                                <Button
                                    onClick={() => convert(detail._id)}
                                    variant="outlined"
                                    disabled={!!detail.convertedToAccountId}
                                    title="Create the parent account without enrolling — useful if you don't have a class in mind yet"
                                >
                                    Create parent account
                                </Button>
                            </Stack>
                            <Stack direction="row" spacing={1}>
                                <IconButton onClick={() => remove(detail._id)}><DeleteIcon /></IconButton>
                                <Button onClick={() => setDetail(null)}>Close</Button>
                            </Stack>
                        </Stack>
                    )}
                </DialogActions>
            </Dialog>
            <Dialog open={newOpen} onClose={() => setNewOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>New waiting-list entry</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {newError && <Alert severity="error" onClose={() => setNewError('')}>{newError}</Alert>}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                label="First name *" size="small" fullWidth
                                value={newForm.firstName}
                                onChange={(e) => setNewForm({ ...newForm, firstName: e.target.value })}
                            />
                            <TextField
                                label="Surname *" size="small" fullWidth
                                value={newForm.surname}
                                onChange={(e) => setNewForm({ ...newForm, surname: e.target.value })}
                            />
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                label="Email *" size="small" fullWidth
                                value={newForm.email}
                                onChange={(e) => setNewForm({ ...newForm, email: e.target.value })}
                            />
                            <TextField
                                label="Phone" size="small" fullWidth
                                value={newForm.phone}
                                onChange={(e) => setNewForm({ ...newForm, phone: e.target.value })}
                            />
                        </Stack>
                        <Divider textAlign="left">Child</Divider>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                label="Child's name" size="small" fullWidth
                                value={newForm.childName}
                                onChange={(e) => setNewForm({ ...newForm, childName: e.target.value })}
                            />
                            <TextField
                                select label="Year group" size="small" fullWidth
                                value={newForm.yearGroup}
                                onChange={(e) => setNewForm({ ...newForm, yearGroup: e.target.value })}
                            >
                                <MenuItem value="">—</MenuItem>
                                {YEAR_GROUP_OPTIONS.map((y) => (
                                    <MenuItem key={y} value={y}>{y}</MenuItem>
                                ))}
                            </TextField>
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                label="Subject" size="small" fullWidth
                                value={newForm.subject}
                                onChange={(e) => setNewForm({ ...newForm, subject: e.target.value })}
                            />
                            <TextField
                                label="School" size="small" fullWidth
                                value={newForm.school}
                                onChange={(e) => setNewForm({ ...newForm, school: e.target.value })}
                            />
                        </Stack>
                        <TextField
                            label="Comments" size="small" fullWidth multiline minRows={2}
                            value={newForm.comments}
                            onChange={(e) => setNewForm({ ...newForm, comments: e.target.value })}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <input
                                type="checkbox" id="sendThankYou"
                                checked={newForm.sendThankYou}
                                onChange={(e) => setNewForm({ ...newForm, sendThankYou: e.target.checked })}
                            />
                            <label htmlFor="sendThankYou">
                                <Typography variant="body2">
                                    Send the thank-you email to this parent
                                </Typography>
                                <Typography variant="caption" color="text.secondary" component="div">
                                    Off by default — admins usually add entries quietly. Tick if you want the
                                    parent to receive the same auto-reply as a self-submission.
                                </Typography>
                            </label>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNewOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={submitNew} disabled={newBusy}>
                        {newBusy ? 'Adding…' : 'Add to waiting list'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

const emptyNewForm = () => ({
    firstName: '',
    surname: '',
    email: '',
    phone: '',
    childName: '',
    yearGroup: '',
    subject: '',
    school: '',
    comments: '',
    sendThankYou: false,
});

const Row = ({ label, value, multi }) => (
    <Box sx={{ display: 'flex', flexDirection: multi ? 'column' : 'row', gap: 1 }}>
        <Typography color="text.secondary" sx={{ minWidth: 180 }}>{label}</Typography>
        <Typography sx={{ whiteSpace: 'pre-wrap' }}>{value || '—'}</Typography>
    </Box>
);

const FLOW_STEPS = [
    { label: 'Received', sub: 'Parent submitted the form' },
    { label: 'Contacted', sub: 'You spoke to the parent' },
    { label: 'Account created', sub: 'Parent account has been created' },
    { label: 'Enrolled', sub: 'Child added to a class roster' },
];

const FlowStepper = ({ status }) => {
    const active = STATUS_STEP[status];
    if (active < 0) {
        return (
            <Alert severity="info" sx={{ py: 0.5 }}>
                This entry was marked <strong>{status}</strong>. Set status back to <em>New</em> or
                <em> Contacted</em> to resume the flow.
            </Alert>
        );
    }
    return (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            {FLOW_STEPS.map((s, i) => {
                const done = i < active;
                const current = i === active;
                return (
                    <Box key={s.label} sx={{ flex: 1, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <Box sx={{
                            width: 24, height: 24, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: done ? 'success.main' : current ? 'primary.main' : 'action.disabledBackground',
                            color: done || current ? '#fff' : 'text.secondary',
                            fontSize: 12, fontWeight: 700, flexShrink: 0,
                        }}>
                            {done ? '✓' : i + 1}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: current ? 'primary.main' : 'text.primary' }}>
                                {s.label}
                            </Typography>
                            <Typography variant="caption" component="div" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                {s.sub}
                            </Typography>
                        </Box>
                    </Box>
                );
            })}
        </Stack>
    );
};
